import { grouped, VIDEO_COMPOSE_TYPICAL_RANGE } from "@/entities/scene-card/model/creditRates";

/**
 * 「AI 智能编排(分镜+台本图)」按钮旁的花费说明文案 —— PC 创建页(`VideoCreatePage`)与企微 H5
 * 创建页(`wecom-h5/advisor/create/VideoCreatePage`)共用同一份,别各写一遍(本仓红线
 * 「同一个数字只许一个出处」的措辞版:两端读的是同一个 `GET /video/compose/estimate`,
 * 拼句子的规则也不该长两份)。
 *
 * 区间/退化规则整个借 `advisorQuote.priceText()`(下沿缺席或 low>=high 时退回单值「约 {high}
 * 积分」,同一条规则,不重新发明)。
 *
 * ★0904(用户反馈「不要让用户觉得免费」):`high` 为 null 此前**一律**整句不渲染 —— 这把「还在
 * 拉」和「拉挂了」焊成了一种表现。链路排查(见同批 commit)证实 console→api→ai 三段路由/鉴权
 * 静态核对全部匹配,不是结构性断链;但 `useComposeEstimate` 对失败只重试一次就落定 `isError`,
 * 那之后**整场会话**这句话再也不会出现 —— 恰是用户会觉得「免费」的那种偶发故障窗口。
 * 现在显式区分「还没拉回来」(`failed=false` → 仍是 `null`,不渲染,给首次加载留出安静的一瞬)
 * 与「拉挂了」(`failed=true` → 兜底句 `COMPOSE_ESTIMATE_FALLBACK`,宁可模糊也不许看起来免费)。
 * 兜底句里「几百至一两千」是**量级描述,不是报价数字** —— ai 侧改 `VIDEO_EST_COMPOSE_*` 常量
 * 时这句话不用跟着改,也不会与真区间打架(两处数字打架是本仓另一条红线)。
 *
 * ★V11(2026-09-04):区间上界不再只是统计承诺,backend 结算面加了硬顶
 * (`xb_price_sku.settle_cap_credits`,与 ai 侧 `VIDEO_EST_COMPOSE_HIGH` 同一个数)——
 * 实扣真的不会超过这句话里报的上界,故文案从「按实际用量结算」升级成「不会超过上限」。
 *
 * ★1006(演示机最终回归):「约 400~1900 积分」两次实扣 235 / 123,都低于下限。编排按 token 计费、
 * 只有封顶**没有下限**,且 api V9 换 gpt-6.1-sol 后单价降到 1/3 左右 —— 接口的 low(ai 侧仍 400)
 * 不再当下限展示。现句式 =「按实际用量,通常 {常见区间},最高 {high}」:常见区间来自
 * `VIDEO_COMPOSE_TYPICAL_RANGE`(依据见其头注),最高 = 接口 high(= 结算硬顶)。上面关于
 * `priceText` 区间退化规则的描述已不适用(不再出现「约 low~high」)。
 */
export const COMPOSE_ESTIMATE_FALLBACK = "按实际用量计费(通常一两百至几百积分),不会超过上限";

/** 「通常 100~500」——上沿不超过 high(封顶比常见区间还低时,常见区间收到封顶为止)。 */
function typicalText(high: number): string {
  const [lo, hiTypical] = VIDEO_COMPOSE_TYPICAL_RANGE;
  const top = Math.min(hiTypical, high);
  return lo < top ? `通常 ${grouped(lo)}~${grouped(top)} 积分` : `通常 ${grouped(top)} 积分以内`;
}

/**
 * `low` 仍收(接口字段与调用方签名不变),但**不展示**:编排没有下限(见头注 1006)。
 */
export function composeEstimateCaption(
  _low: number | null,
  high: number | null,
  failed = false
): string | null {
  if (high == null) return failed ? COMPOSE_ESTIMATE_FALLBACK : null;
  return `按实际用量结算 · ${typicalText(high)},最高 ${grouped(high)}(不会超过)`;
}
